// PhaseTabs — horizontal tab strip for phase-detail pages.
//
// Each phase page in FORGE (Interest, Application, BOD Vote, Training,
// Year 1 Active) uses this to slice its content into named angles.
// Active tab is reflected in `?tab=<key>` so bookmarks + refresh keep state.

import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

export interface PhaseTab {
  key: string;
  label: string;
  content: React.ReactNode;
  /** Optional caption that sits to the right of the tab label. */
  badge?: string | number;
  /** When true, this tab is only a placeholder — no content yet. */
  placeholder?: boolean;
}

export interface PhaseTabsProps {
  /** Short section/phase label (e.g. "FORGE") shown above the page title. */
  eyebrow?: string;
  title: string;
  subtitle?: string;
  tabs: PhaseTab[];
  /** Tab key to select when the URL has no ?tab param. Defaults to first. */
  defaultTab?: string;
  /** Optional right-aligned header content (e.g. LiveIndicator). */
  headerRight?: React.ReactNode;
}

export default function PhaseTabs({
  eyebrow,
  title,
  subtitle,
  tabs,
  defaultTab,
  headerRight,
}: PhaseTabsProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const firstKey = tabs[0]?.key ?? "";
  const activeKey = useMemo(() => {
    const paramKey = searchParams.get("tab");
    if (paramKey && tabs.some((t) => t.key === paramKey)) return paramKey;
    return defaultTab && tabs.some((t) => t.key === defaultTab)
      ? defaultTab
      : firstKey;
  }, [searchParams, tabs, defaultTab, firstKey]);

  const selectTab = useCallback(
    (key: string) => {
      const next = new URLSearchParams(searchParams);
      if (key === firstKey || (defaultTab && key === defaultTab)) {
        next.delete("tab");
      } else {
        next.set("tab", key);
      }
      navigate(
        {
          pathname: location.pathname,
          search: next.toString() ? `?${next.toString()}` : "",
        },
        { replace: true },
      );
      setSearchParams(next, { replace: true });
    },
    [
      searchParams,
      setSearchParams,
      navigate,
      location.pathname,
      firstKey,
      defaultTab,
    ],
  );

  const active = tabs.find((t) => t.key === activeKey) ?? tabs[0];

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      {/* ── Header ─────────────────────────────────────────────── */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          {eyebrow && (
            <p className="text-[11px] font-bold tracking-[0.14em] uppercase text-brand-adaptive mb-1">
              {eyebrow}
            </p>
          )}
          <h1 className="text-2xl font-black text-theme-primary tracking-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="text-theme-secondary mt-1 text-sm">{subtitle}</p>
          )}
        </div>
        {headerRight && <div className="shrink-0">{headerRight}</div>}
      </header>

      {/* ── Tab bar ────────────────────────────────────────────── */}
      <div className="border-b border-theme-subtle -mx-2 md:-mx-0">
        <nav
          className="flex gap-1 overflow-x-auto px-2 md:px-0"
          aria-label="Phase sections"
        >
          {tabs.map((tab) => {
            const isActive = tab.key === active?.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => selectTab(tab.key)}
                className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  isActive
                    ? "text-theme-primary border-brand-adaptive"
                    : "text-theme-muted border-transparent hover:text-theme-secondary hover:border-theme-subtle"
                } ${tab.placeholder ? "opacity-60" : ""}`}
                aria-current={isActive ? "page" : undefined}
              >
                <span>{tab.label}</span>
                {tab.badge !== undefined && (
                  <span className="ml-2 inline-block text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-theme-overlay">
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* ── Content ────────────────────────────────────────────── */}
      <div>{active?.content}</div>
    </div>
  );
}

/** Lightweight placeholder content used by "coming soon" tabs. */
export function PlaceholderTab({ hint }: { hint: string }) {
  return (
    <div className="glass rounded-xl p-12 flex flex-col items-center justify-center text-center gap-3">
      <svg
        width="32"
        height="32"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        className="text-theme-muted"
      >
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
      <p className="text-sm font-semibold text-theme-secondary">Coming soon</p>
      <p className="text-xs text-theme-muted max-w-md">{hint}</p>
    </div>
  );
}
